/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  AppBar,
  Toolbar,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

import aws from 'aws-sdk';
import dayjs from 'dayjs';
import crypto from 'crypto-js';

import BackgroundBox from '@/components/common/BackgroundBox';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import ErrorDialog from '@/components/common/ErrorDialog';

import * as ui from '@/utils/uiUtil';
import { File } from 'aws-sdk/clients/codecommit';

aws.config.apiVersions = {
  s3: '2006-03-01',
};

const K = '78h0nvq234h0qklihjregh9803f4';

type State = {
  parametersValid: boolean;
  fileListTableRows: FileListTableRow[];
  loading: boolean;
  topDir: string;
  currentDir: string;
  configKeyEncoded: string;
  uploadingFiles?: any[];
  deletingFile: string; // S3 key
};

type DialogState = {
  open: boolean;
  title?: string;
  content: string;
  confirmLabel?: string;
};

type FileListTableRow = {
  fileName: string;
  key: string;
  lastModified?: Date;
  size?: number;
  downloadUrl?: string;
  isDirectory: boolean;
};

const Project: React.FC = () => {
  const location = useLocation();

  const [state, setState] = useState<State>({
    parametersValid: false,
    fileListTableRows: [],
    loading: false,
    topDir: '',
    currentDir: '',
    configKeyEncoded: '',
    uploadingFiles: undefined,
    deletingFile: '',
  });

  const [overwriteConfirmDialog, setOverwriteConfirmDialog] = useState<DialogState>({
    open: false,
    content: '',
  });

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<DialogState>({
    open: false,
    content: '',
  });

  const [errorDialog, setErrorDialog] = useState<DialogState>({
    open: false,
    content: '',
  });

  useEffect(() => {
    const qParams = getSettingsFromQueryParams(location.search);

    state.parametersValid = Boolean(qParams.bucket && qParams.prefix && qParams.accessKeyId && qParams.secretAccessKey);
    setState({ ...state });
    if (!state.parametersValid) {
      return;
    }

    if (!qParams.configKey) {
      const configKey = crypto.AES.encrypt(document.location.search, K).toString();
      document.location.href = document.location.pathname + '?ckey=' + encodeURIComponent(configKey);
      return;
    } else {
      state.configKeyEncoded = encodeURIComponent(qParams.configKey);
      setState({ ...state });
    }

    refreshFileList();
    const interval = setInterval(() => {
      updateDownloadUrl();
    }, 1000 * 60 * 15); // 15分ごと
    return () => clearInterval(interval);
  }, [location]);

  const generatePresignedUrl = async (operation: string, bucket: string, key: string, s3: aws.S3) => {
    const url = await s3.getSignedUrlPromise(operation, {
      Bucket: bucket,
      Key: key,
      Expires: 60 * 60,
    });
    return url;
  };

  const getSettingsFromQueryParams = (search: string) => {
    const originalParams = new URLSearchParams(search);
    const configKey = originalParams.get('ckey') ?? undefined;

    let params;
    if (configKey) {
      const decrypted = crypto.AES.decrypt(configKey, K).toString(crypto.enc.Utf8);
      params = new URLSearchParams(decrypted);
    } else {
      params = new URLSearchParams(document.location.search);
    }
    const bucket = params.get('bucket') ?? '';
    const prefix = params.get('prefix') ?? '';
    const accessKeyId = params.get('keyid') ?? '';
    const secretAccessKey = params.get('secret') ?? '';
    // currentDirはoriginalParamsを優先
    const currentDir = originalParams.get('dir') || params.get('dir') || '';
    return { configKey, bucket, prefix, accessKeyId, secretAccessKey, currentDir };
  };

  const refreshFileList = async (showLoadingSpinner: boolean = true) => {
    if (showLoadingSpinner) {
      state.loading = true;
      setState({ ...state });
    }

    const { bucket, prefix, accessKeyId, secretAccessKey, currentDir } = getSettingsFromQueryParams(location.search);

    if (!bucket || !prefix || !accessKeyId || !secretAccessKey) {
      return;
    }

    state.currentDir = currentDir;
    const lastSlashIndex = prefix.lastIndexOf('/');
    // prefixに含まれる最後の/までがディレクトリ名
    const topDir = prefix.substring(0, lastSlashIndex + 1);
    state.topDir = topDir;
    setState({ ...state });

    try {
      const currentPrefix = topDir + currentDir;
      const s3 = new aws.S3({
        credentials: {
          accessKeyId,
          secretAccessKey,
        },
      });

      const objectList = await listS3Objects(bucket, currentPrefix, s3);
      if (!objectList) {
        return;
      }

      const tmpFileListTableRows = objectList
        .filter((obj) => {
          // フォルダ要素は除外
          return !obj.Key!!.endsWith('/');
        })
        .map((obj) => {
          const key = obj.Key!!;
          const isDirectory = obj.Key!!.endsWith('/');
          const parentDirSlashIndex = key.substring(0, key.length - 1).lastIndexOf('/');
          return {
            fileName: key.substring(parentDirSlashIndex + 1),
            key,
            lastModified: obj.LastModified!!,
            size: obj.Size!!,
            isDirectory,
          } as FileListTableRow;
        });

      // サブフォルダのファイルはフォルダ要素として集約
      const fileListTableRows = [] as FileListTableRow[];
      let previousName = '';
      for (const row of tmpFileListTableRows) {
        const relPath = row.key.substring(currentPrefix.length);
        if (relPath.includes('/')) {
          const dirName = relPath.split('/')[0] + '/';
          if (previousName !== dirName) {
            fileListTableRows.push({
              fileName: dirName,
              key: currentPrefix + dirName,
              isDirectory: true,
            } as FileListTableRow);
            previousName = dirName;
          }
        } else {
          fileListTableRows.push(row);
          previousName = row.fileName;
        }
      }
      state.fileListTableRows = fileListTableRows;

      // ダウンロードURLを作成
      await updateDownloadUrl();
      state.loading = false;
      setState({ ...state });
    } catch (e) {
      state.parametersValid = false;
      setState({ ...state });
    }
  };

  const updateDownloadUrl = async () => {
    const { bucket, accessKeyId, secretAccessKey } = getSettingsFromQueryParams(location.search);
    const s3 = new aws.S3({
      credentials: { accessKeyId, secretAccessKey },
    });
    for (const row of state.fileListTableRows.filter((row) => !row.isDirectory)) {
      row.downloadUrl = await generatePresignedUrl('getObject', bucket, row.key, s3);
    }
    console.log('URL updated.');
    setState({ ...state });
  };

  const listS3Objects = async (bucket: string, prefix: string, s3: aws.S3) => {
    const params = { Bucket: bucket, Prefix: prefix };
    const data = await s3.listObjectsV2(params).promise();
    return data.Contents;
  };

  const deleteFile = async (key: string) => {
    if (!key) {
      return;
    }

    const { bucket, accessKeyId, secretAccessKey } = getSettingsFromQueryParams(location.search);
    const s3 = new aws.S3({
      credentials: {
        accessKeyId,
        secretAccessKey,
      },
    });
    await s3.deleteObject({ Bucket: bucket, Key: key }).promise();
    refreshFileList(false);
  };

  const getFolderLinkUrl = (dir: string) => {
    return location.pathname + `?ckey=${state.configKeyEncoded}&dir=${dir}`;
  };

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      console.log(acceptedFiles);
      if (acceptedFiles.length > 10) {
        return;
      }

      const { bucket, accessKeyId, secretAccessKey } = getSettingsFromQueryParams(location.search);
      const s3 = new aws.S3({
        credentials: {
          accessKeyId,
          secretAccessKey,
        },
      });

      // currentDir以下のS3存在ファイル一覧を取得し、その中にacceptedFilesのアップロード先が含まれているかチェック
      const existingKeyList =
        (await listS3Objects(bucket, state.topDir + state.currentDir, s3))
          ?.map((obj) => obj.Key)
          .filter((key) => key) ?? [];

      const existingPathList = acceptedFiles
        .filter((file: any) => {
          const key = state.topDir + state.currentDir + file.path.replace(/^\//, '');
          return existingKeyList.includes(key);
        })
        .map((file: any) => file.path);

      if (existingPathList.length > 0) {
        overwriteConfirmDialog.content = existingPathList.join('\n');
        overwriteConfirmDialog.open = true;
        setOverwriteConfirmDialog({ ...overwriteConfirmDialog });
        state.uploadingFiles = acceptedFiles;
      } else {
        await uploadFiles(acceptedFiles);
        state.uploadingFiles = undefined;
      }
    },
    multiple: true,
    maxFiles: 200,
    maxSize: 1024 * 1024 * 10, // 10MBまで
  });

  const uploadFiles = async (acceptedFiles?: any[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }

    const { bucket, accessKeyId, secretAccessKey } = getSettingsFromQueryParams(location.search);
    const s3 = new aws.S3({
      credentials: {
        accessKeyId,
        secretAccessKey,
      },
    });

    for (const file of acceptedFiles) {
      const key = state.topDir + state.currentDir + (file as any).path.replace(/^\//, '');
      await s3
        .upload({
          Bucket: bucket,
          Key: key,
          Body: await file.arrayBuffer(),
          ContentType: 'application/octet-stream',
        })
        .promise();
    }

    refreshFileList(false);
  };

  return (
    <>
      {state.parametersValid ? (
        <>
          <Box sx={{ flexGrow: 1 }}>
            <AppBar position="fixed">
              <Toolbar>
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                  File Browser
                </Typography>
                {/* <Box sx={{ mr: 2 }}>{userEmail}</Box> */}
                {/* <Button color="inherit" onClick={logout}>
          ログアウト
        </Button> */}
              </Toolbar>
            </AppBar>
          </Box>

          <BackgroundBox>
            <Container>
              <Stack sx={{ gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">/{state.currentDir}</Typography>
                </Box>
                <Card>
                  <CardContent>
                    <div
                      {...getRootProps()}
                      style={{
                        cursor: 'pointer',
                        border: isDragActive ? '3px solid #a0e0f0' : '3px solid #e8e8e8',
                        backgroundColor: isDragActive ? '#c0e0f0' : '#f0f0f0',
                        padding: '1.5em',
                        margin: '25px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CloudUploadIcon />
                        &nbsp; Drag &amp; Drop files or Click here to upload files.
                      </div>
                    </div>

                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '50%;' }}>File Name</TableCell>
                          <TableCell sx={{ width: '25%;' }}>Last Modified</TableCell>
                          <TableCell sx={{ width: '25%;' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      {state.loading ? (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={3}>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ) : (
                        <TableBody>
                          {state.currentDir.includes('/') && (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <ArrowUpwardIcon />
                                  &nbsp;
                                  <a href={getFolderLinkUrl(state.currentDir.replace(/[^/]+\/$/g, ''))}>
                                    Back to Parent ..
                                  </a>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {state.fileListTableRows.map((file) => (
                            <TableRow key={file.key}>
                              <TableCell>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  {file.isDirectory ? (
                                    <>
                                      <FolderIcon />
                                      &nbsp;
                                      <a href={getFolderLinkUrl(state.currentDir + file.fileName)}>{file.fileName}</a>
                                    </>
                                  ) : (
                                    <>
                                      <InsertDriveFileRoundedIcon />
                                      &nbsp;{file.fileName}
                                    </>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{dayjs(file.lastModified).format('DD-MMM-YYYY HH:mm')}</TableCell>
                              <TableCell>
                                {!file.isDirectory && (
                                  <>
                                    <a href={file.downloadUrl} download={file.fileName}>
                                      Download
                                    </a>
                                    &nbsp;
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setState({ ...state, deletingFile: file.key });
                                        setDeleteConfirmDialog({
                                          ...deleteConfirmDialog,
                                          content: file.fileName,
                                          open: true,
                                        });
                                      }}
                                    >
                                      Delete
                                    </a>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      )}
                    </Table>
                  </CardContent>
                </Card>
              </Stack>
            </Container>
            <ConfirmDialog
              open={overwriteConfirmDialog.open}
              title="Overwrite the existing files below?"
              content={overwriteConfirmDialog.content}
              confirmLabel="OK"
              cancelable={true}
              cancelLabel="Cancel"
              onConfirm={async () => {
                await uploadFiles(state.uploadingFiles);
                setOverwriteConfirmDialog({ ...overwriteConfirmDialog, open: false });
              }}
              onCancel={() => setOverwriteConfirmDialog({ ...overwriteConfirmDialog, open: false })}
              onClose={() => setOverwriteConfirmDialog({ ...overwriteConfirmDialog, open: false })}
            />
            <ConfirmDialog
              open={deleteConfirmDialog.open}
              title="Deleting file."
              content={deleteConfirmDialog.content}
              confirmLabel={'OK'}
              cancelable={true}
              cancelLabel={'Cancel'}
              onConfirm={async () => {
                await deleteFile(state.deletingFile);
                setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false });
              }}
              onCancel={() => {
                setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false });
              }}
              onClose={() => {
                setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false });
              }}
            />

            <ErrorDialog
              open={errorDialog.open}
              content={errorDialog.content}
              onConfirm={() => setErrorDialog({ ...errorDialog, open: false })}
            />
          </BackgroundBox>
        </>
      ) : (
        <h3 style={{ marginLeft: '20px' }}>Invalid Parameters.</h3>
      )}
    </>
  );
};

export default Project;
